import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CategoryFilter from '../components/CategoryFilter';
import GameCategory from '../components/GameCategory';
import SearchBar from '../components/SearchBar';
import gamesData from '../data/games.json';

interface Game {
  id: number;
  name: string;
  description: string;
  thumbnail: string;
  category: string;
  url: string;
  featured: boolean;
}

const HomePage: React.FC = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState("All");
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [displayMode, setDisplayMode] = useState<'categories' | 'filtered'>('categories');
  
  // Extract query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    const searchParam = params.get('search');
    const filterParam = params.get('filter');
    
    if (categoryParam) {
      setActiveCategory(categoryParam);
      setDisplayMode('filtered');
    } else if (searchParam) {
      // Handle search
      const searchResults = gamesData.games.filter(game => 
        game.name.toLowerCase().includes(searchParam.toLowerCase()) ||
        game.description.toLowerCase().includes(searchParam.toLowerCase())
      );
      setFilteredGames(searchResults);
      setActiveCategory("All");
      setDisplayMode('filtered');
    } else if (filterParam) {
      // Handle filter (Played, New, Trending)
      let filtered;
      switch (filterParam) {
        case 'played':
          filtered = gamesData.games.filter(game => Math.random() > 0.5); // Simulating played games
          break;
        case 'new':
          filtered = [...gamesData.games].sort((a, b) => b.id - a.id).slice(0, 8); // Newest games
          break;
        case 'trending':
          filtered = gamesData.games.filter(game => game.featured); // Featured as trending
          break;
        default:
          filtered = gamesData.games;
      }
      setFilteredGames(filtered);
      setActiveCategory("All");
      setDisplayMode('filtered');
    } else {
      setActiveCategory("All");
      setDisplayMode('categories');
    }
  }, [location.search]);
  
  // Handle category selection
  const handleCategorySelect = (category: string) => {
    if (category === "All") {
      setActiveCategory("All");
      setDisplayMode('categories');
      // Reset URL without refreshing page
      window.history.pushState({}, '', '/');
    } else {
      setActiveCategory(category);
      setDisplayMode('filtered');
      // Update URL without refreshing page
      window.history.pushState({}, '', `/?category=${category}`);
    }
  };
  
  // Filter games for the current active category
  useEffect(() => {
    if (activeCategory === "All") {
      setFilteredGames(gamesData.games);
    } else {
      const filtered = gamesData.games.filter(game => game.category === activeCategory);
      setFilteredGames(filtered);
    }
  }, [activeCategory]);
  
  // Group games by category for category display mode
  const gamesByCategory = gamesData.categories
    .filter(category => category !== "All")
    .map(category => ({
      title: category,
      games: gamesData.games.filter(game => game.category === category)
    }));
  
  return (
    <div className="min-h-screen">
      {/* Hero/Banner Section */}
      <section className="bg-gradient-to-r from-dark to-dark-light py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Discover Amazing Games
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Play hundreds of free online games without downloading. Find your next favorite game!
          </p>
          <div className="max-w-md mx-auto">
            <SearchBar />
          </div>
        </div>
      </section>
      
      {/* Category Filter */}
      <section className="bg-dark-dark py-4 sticky top-0 z-10 shadow-md">
        <div className="container-custom">
          <CategoryFilter 
            categories={gamesData.categories}
            activeCategory={activeCategory}
            onSelectCategory={handleCategorySelect}
          />
        </div>
      </section>
      
      {/* Games Display */}
      <div className="py-8">
        {displayMode === 'categories' ? (
          // Category Mode: Show games grouped by category
          gamesByCategory.map(category => (
            <GameCategory 
              key={category.title}
              title={category.title}
              games={category.games}
            />
          ))
        ) : (
          // Filtered Mode: Show all games of selected category
          <section className="container-custom">
            <h2 className="text-2xl font-bold text-white mb-8">
              {activeCategory === "All" ? "All Games" : activeCategory}
            </h2>
            
            {filteredGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredGames.map(game => (
                  <div key={game.id} className="mb-6">
                    <GameCategory 
                      title=""
                      games={[game]}
                      limit={1}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No games found.</p>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default HomePage; 